/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import React from 'react';
import { WMDefaultStyleName } from '@opengeoweb/webmap';
import type { Dimension } from '@opengeoweb/webmap';
import { GeoTIFFLayer } from './GeoTIFFLayer';
import type { OpenLayersFeatureLayerProps } from './OpenLayersFeatureLayer';
import {
  STAC_GEOTIFF_FRAME_PRELOAD_RADIUS,
  STAC_ITEM_LOOKUP_WINDOW_INTERVAL_MULTIPLIER,
  STAC_ITEM_LOOKUP_WINDOW_MS,
  getFrameStepInMilliseconds,
  getLookupStepInMilliseconds,
  getRequiredLookupMarginInMilliseconds,
  getStacFrameItemsToRender,
  getStacFrameTargetTimes,
  getStacItemLookupWindowForTimes,
  getTimeIntervalInMilliseconds,
  isStacItemLookupWindowValidForTime,
  resolvePreferredStyleName,
  shouldPreloadStacFrames,
} from './TimeAwareGeoTIFFLayerUtils';
import type { STACItemLookupWindow } from './TimeAwareGeoTIFFLayerUtils';
import {
  useStacItems,
  useStacCollection,
} from '../../../geotiff-layer-api/hooks';
import { stacColormapToOlStyle } from '../../../geotiff-layer-api/utils/stacColormapToOlStyle';
import { useGetStacGeoTIFFLayerInstance } from '../../../geotiff-layer-api/utils/useGetStacGeoTIFFLayerInstance';
import TimeContext from '../context/TimeContext';
import { getDimensionParamsForWMS } from '../utils/makeDimValuesToPrefetch';
import type { OnInitializeLayerProps } from '../utils/types';

export { getStacFrameItemsToRender, getStacItemLookupWindowForTimes };

interface TimeAwareGeoTIFFLayerProps extends OpenLayersFeatureLayerProps {
  layerId?: string;
  stacCollectionUrl: string;
  layerName: string;
  styleName?: string;
  dimensions?: Dimension[];
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  opacity?: number;
  visible?: boolean;
}

export const TimeAwareGeoTIFFLayer: React.FC<TimeAwareGeoTIFFLayerProps> = ({
  stacCollectionUrl,
  layerName,
  style,
  styleName,
  zIndex,
  opacity,
  dimensions = [],
  layerId,
  visible = true,
  onInitializeLayer,
}) => {
  const { prefetchList = [] } = React.useContext(TimeContext);
  const wmLayer = useGetStacGeoTIFFLayerInstance(
    stacCollectionUrl,
    layerName,
    layerId,
    onInitializeLayer,
  );

  const { data: collectionData } = useStacCollection(stacCollectionUrl);

  const effectiveStyleName = React.useMemo(() => {
    const renderNames = Object.keys(collectionData?.renders || {});
    return resolvePreferredStyleName(renderNames, styleName);
  }, [collectionData, styleName]);

  const geotiffStyle = React.useMemo(() => {
    const renderEntry = collectionData?.renders?.[effectiveStyleName];
    if (renderEntry?.colormap) {
      return stacColormapToOlStyle(renderEntry.colormap);
    }

    return undefined;
  }, [effectiveStyleName, collectionData]);

  const shouldNormalizeGeoTiff =
    effectiveStyleName === WMDefaultStyleName &&
    !collectionData?.renders?.[effectiveStyleName]?.colormap;

  const dimensionsValues = getDimensionParamsForWMS(dimensions, wmLayer);
  const timeValue = dimensionsValues?.['TIME'];
  const timeIntervalMs = getTimeIntervalInMilliseconds(
    wmLayer?.getDimension('time')?.getDimInterval(),
  );
  const prefetchListEffectCheck = prefetchList.join(',');
  const frameStepMs = React.useMemo(
    () => getFrameStepInMilliseconds(timeValue, prefetchList, timeIntervalMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefetchListEffectCheck, timeIntervalMs, timeValue],
  );
  const lookupStepMs = getLookupStepInMilliseconds(frameStepMs, timeIntervalMs);
  const shouldPreloadFrames = shouldPreloadStacFrames(
    frameStepMs,
    timeIntervalMs,
  );
  const preloadRadius = shouldPreloadFrames
    ? STAC_GEOTIFF_FRAME_PRELOAD_RADIUS
    : 0;
  const lookupWindowMs = lookupStepMs
    ? lookupStepMs * STAC_ITEM_LOOKUP_WINDOW_INTERVAL_MULTIPLIER
    : STAC_ITEM_LOOKUP_WINDOW_MS;
  const frameTargetTimes = React.useMemo(
    () =>
      getStacFrameTargetTimes(
        timeValue,
        prefetchList,
        frameStepMs,
        preloadRadius,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [frameStepMs, prefetchListEffectCheck, preloadRadius, timeValue],
  );
  const requiredLookupMarginMs = getRequiredLookupMarginInMilliseconds(
    timeValue,
    frameTargetTimes,
    lookupStepMs ? lookupStepMs / 2 : lookupWindowMs / 2,
  );

  const [requestedStartDate, requestedEndDate] = React.useMemo(
    () =>
      getStacItemLookupWindowForTimes(
        timeValue ? [timeValue] : [],
        lookupWindowMs,
      ),
    [timeValue, lookupWindowMs],
  );

  const [activeItemLookupWindow, setActiveItemLookupWindow] =
    React.useState<STACItemLookupWindow>([undefined, undefined]);
  const isActiveItemLookupWindowValid = isStacItemLookupWindowValidForTime(
    activeItemLookupWindow,
    timeValue,
    requiredLookupMarginMs,
  );
  const [activeStartDate, activeEndDate] = activeItemLookupWindow;
  const startDate = isActiveItemLookupWindowValid
    ? activeStartDate
    : requestedStartDate;
  const endDate = isActiveItemLookupWindowValid
    ? activeEndDate
    : requestedEndDate;

  React.useEffect(() => {
    setActiveItemLookupWindow((currentWindow) => {
      if (
        isStacItemLookupWindowValidForTime(
          currentWindow,
          timeValue,
          requiredLookupMarginMs,
        )
      ) {
        return currentWindow;
      }

      if (
        currentWindow[0] === requestedStartDate &&
        currentWindow[1] === requestedEndDate
      ) {
        return currentWindow;
      }

      return [requestedStartDate, requestedEndDate];
    });
  }, [requestedEndDate, requestedStartDate, requiredLookupMarginMs, timeValue]);

  const { data: items, isLoading } = useStacItems(
    stacCollectionUrl,
    startDate,
    endDate,
  );

  const frameItems = React.useMemo(
    () => getStacFrameItemsToRender(items, timeValue, frameTargetTimes),
    [frameTargetTimes, items, timeValue],
  );

  React.useLayoutEffect(() => {
    frameItems.forEach(({ item }) => {
      const wkt2 = item.properties['proj:wkt2'];

      if (wkt2) {
        registerProjection(wkt2);
      }
    });
  }, [frameItems]);

  if (!items || isLoading || !startDate || !endDate) {
    return null;
  }

  if (
    !isStacItemLookupWindowValidForTime(
      [startDate, endDate],
      timeValue,
      requiredLookupMarginMs,
    )
  ) {
    return null;
  }

  if (frameItems.length === 0) {
    return null;
  }

  return (
    <>
      {frameItems.map(({ item, isSelected }) => (
        <GeoTIFFLayer
          geotiffStyle={geotiffStyle}
          key={`${item.id}-${effectiveStyleName}`}
          layerName={item.id}
          layerOptions={{
            opacity: isSelected ? (opacity ?? 1) : 0,
            zIndex: isSelected ? zIndex : (zIndex ?? 0) - 1,
          }}
          layerProps={{
            enabled: visible,
          }}
          normalize={shouldNormalizeGeoTiff}
          projection={item.properties['proj:wkt2']}
          url={item.assets['data'].href}
        />
      ))}
    </>
  );
};

const registerProjection = (wkt2: string): void => {
  if (!proj4.defs(wkt2)) {
    proj4.defs(wkt2, wkt2);
    register(proj4);
  }
};
